import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// navigator.onLine only reflects "attached to a network", not "API reachable"
// (captive wifi portals, building wifi with no internet uplink, etc. all
// report true). We treat isOnline as true only once an active ping to the
// Flask API has actually succeeded.
const ConnectivityContext = createContext({ isOnline: true, recheckNow: async () => true });

const PING_TIMEOUT_MS = 4000;
const POLL_INTERVAL_MS = 15000;
const FAILURES_BEFORE_OFFLINE = 2;

async function pingApi() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/ping`, {
            method: 'GET',
            cache: 'no-store',
            signal: controller.signal,
        });
        return response.ok;
    } catch (error) {
        return false;
    } finally {
        clearTimeout(timeoutId);
    }
}

export function OnlineStatusProvider({ children }) {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const consecutiveFailuresRef = useRef(0);
    const pollTimerRef = useRef(null);

    const applyPingResult = useCallback((reachable) => {
        if (reachable) {
            consecutiveFailuresRef.current = 0;
            setIsOnline(true);
        } else {
            consecutiveFailuresRef.current += 1;
            if (consecutiveFailuresRef.current >= FAILURES_BEFORE_OFFLINE) {
                setIsOnline(false);
            }
        }
    }, []);

    const recheckNow = useCallback(async () => {
        if (!navigator.onLine) {
            consecutiveFailuresRef.current = FAILURES_BEFORE_OFFLINE;
            setIsOnline(false);
            return false;
        }
        const reachable = await pingApi();
        applyPingResult(reachable);
        return reachable;
    }, [applyPingResult]);

    useEffect(() => {
        recheckNow();

        const handleBrowserOnline = () => recheckNow();
        const handleBrowserOffline = () => {
            consecutiveFailuresRef.current = FAILURES_BEFORE_OFFLINE;
            setIsOnline(false);
        };

        window.addEventListener('online', handleBrowserOnline);
        window.addEventListener('offline', handleBrowserOffline);

        return () => {
            window.removeEventListener('online', handleBrowserOnline);
            window.removeEventListener('offline', handleBrowserOffline);
        };
    }, [recheckNow]);

    // While offline (or not yet verified), poll on a low-frequency interval so
    // the app recovers automatically once signal comes back, with no user action.
    useEffect(() => {
        if (isOnline) {
            clearTimeout(pollTimerRef.current);
            return undefined;
        }
        const poll = async () => {
            await recheckNow();
            pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        };
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        return () => clearTimeout(pollTimerRef.current);
    }, [isOnline, recheckNow]);

    return (
        <ConnectivityContext.Provider value={{ isOnline, recheckNow }}>
            {children}
        </ConnectivityContext.Provider>
    );
}

export function useOnlineStatus() {
    return useContext(ConnectivityContext);
}
